import React, { useEffect, useMemo, useState } from "react";
import AttachmentIcon from "assets/medication/attachments.svg";
import DocumentsIcon from "assets/medication/documents.svg";
import EditIcon from "assets/medication/icons.svg";
import moment from "moment";
import { convertTo12HourFormat, getDayMonthTableHeader } from "Utils/utils";
import EditModal from "./components/EditModal";

const HistoryTable = ({ item, toDate, fromDate, participantInfo }) => {
  const [openEditModal, setEditModal] = useState(false);
  const [singleData, setSingleDate] = useState(null);
  const [updatedData, setUpdatedData] = useState(null);
  const [medicationLogs, setMedicationLogs] = useState(
    item?.participant_medication_logs || []
  );

  useEffect(() => {
    if (updatedData) {
      setMedicationLogs((prevLogs) =>
        prevLogs.map((log) =>
          log.id === updatedData.id ? { ...log, ...updatedData } : log
        )
      );
    }
  }, [updatedData]);

  const headerData = useMemo(() => {
    if (fromDate && toDate) {
      return getDayMonthTableHeader(fromDate, toDate);
    }
  }, [toDate, fromDate]);

  const handleEditClick = (item) => {
    setSingleDate(item);
    setEditModal(true);
  };

  const getLogForDate = (date) => {
    const log = medicationLogs.find((log) =>
      moment(log.date).isSame(moment(date), "day")
    );
    return log ? log : null;
  };

  const notesByWorkerLabel = (log) => {
    if (!log?.notes || !String(log.notes).trim()) {
      return "";
    }
    return String(log.notes).trim();
  };
  
  return (
    <>
      {/* Medication Card Header */}
      <div className='bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 px-4 sm:px-6 py-4'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div className='flex items-center gap-3'>
            <div className='flex-shrink-0'>
              <div className='h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center'>
                <svg className='w-5 h-5 text-cyan-600' fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
            </div>
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>
                Medicine Name: <span className='text-cyan-700 font-bold'>{item?.medicine_name}</span>
              </h3>
              <p className='text-xs text-gray-600 mt-0.5'>
                {fromDate && toDate
                  ? `${moment(fromDate).format("MMM DD YYYY")} - ${moment(toDate).format("MMM DD YYYY")}`
                  : ""}
              </p>
              {(item?.updated_by_name || item?.updater?.name) && (
                <p className='text-xs text-gray-600 mt-1.5'>
                  <span className='font-semibold text-gray-700'>Medication updated by: </span>
                  {item?.updated_by_name || item?.updater?.name}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='p-4 sm:p-6'>
        <div className='overflow-x-auto custom-scroll'>
          <table
            className='table-auto w-full border-collapse'
            style={{ tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                <th
                  className='text-xs font-semibold px-4 py-3 bg-gray-100 border border-gray-200 sticky left-0 z-[2] text-center'
                  style={{ width: "150px", minWidth: "150px" }}
                >
                  Date
                </th>
                {headerData?.map((item, index) => {
                  return (
                    <th
                      key={index}
                      className='border border-gray-200 whitespace-nowrap p-2 px-3 text-gray-700 bg-gray-50 font-semibold text-center text-xs'
                      style={{ width: "100px", minWidth: "100px" }}
                    >
                      {`${moment(item).format("DD-MMM")}`}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  className='text-xs font-semibold px-4 py-3 bg-gray-100 border border-gray-200 sticky left-0 z-[1] text-center'
                  style={{ width: "150px", minWidth: "150px" }}
                >
                  <span className='text-gray-700'>
                    {item?.event_type === "time"
                      ? convertTo12HourFormat(item?.time)
                      : item?.event_type === "prn"
                      ? "PRN"
                      : item?.event_name}
                  </span>
                </td>
                {headerData?.map((date, index) => {
                  const log = getLogForDate(date);

                  return (
                    <td
                      key={index}
                      className={`border border-gray-200 py-3 px-2 font-semibold text-center text-xs ${
                        log?.status_letter === "R"
                          ? "bg-red-50"
                          : log?.status_letter === "G"
                          ? "bg-green-50"
                          : "bg-white"
                      }`}
                      style={{ width: "100px", minWidth: "100px" }}
                    >
                      <div className='flex items-center justify-center gap-1.5'>
                        {log ? (
                          <>
                            <div
                              className={`relative text-base font-bold ${
                                log?.status_letter === "G"
                                  ? "text-green-600"
                                  : log?.status_letter === "R"
                                  ? "text-red-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {log?.status_letter}
                            </div>
                            <div
                              className='relative'
                              title={log?.notes ? notesByWorkerLabel(log) : undefined}
                            >
                              <img
                                src={DocumentsIcon}
                                alt='document icon'
                                className='w-4 h-4'
                              />
                              {log?.notes && (
                                <div className='absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center'>
                                  1
                                </div>
                              )}
                            </div>

                            <div className='relative'>
                              <img
                                src={AttachmentIcon}
                                alt='attachment icon'
                                className='w-4 h-4'
                              />
                              {log?.attachments &&
                                Array.isArray(JSON.parse(log?.attachments)) &&
                                JSON.parse(log?.attachments).length > 0 && (
                                  <div className='absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center'>
                                    {JSON.parse(log?.attachments).length}
                                  </div>
                                )}
                            </div>
                            <img
                              src={EditIcon}
                              alt='edit icon'
                              onClick={() => handleEditClick(log)}
                              title='Edit'
                              className='cursor-pointer w-4 h-4 hover:opacity-70 transition-opacity'
                            />
                          </>
                        ) : (
                          <span className='text-gray-300'>—</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {openEditModal && (
        <EditModal
          openEditModal={openEditModal}
          setEditModal={setEditModal}
          singleData={singleData}
          participantInfo={participantInfo}
          setUpdatedData={setUpdatedData}
        />
      )}
    </>
  );
};

export default HistoryTable;
